import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api.ts';
import { Z_INDEX_CLASSES } from '../constants/zIndex.ts';

interface StatusHistoryEntry {
  id: number;
  orderId: number;
  previousStatus: string | null;
  newStatus: string;
  changeReason: string | null;
  notes: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  updatedAt: string;
  changedByUser?: {
    id: number;
    name: string;
    email: string;
    role: string;
  };
}

interface OrderStatusHistoryProps {
  orderId: number;
  isOpen: boolean;
  onClose: () => void;
}

const OrderStatusHistory: React.FC<OrderStatusHistoryProps> = ({ orderId, isOpen, onClose }) => {
  const [history, setHistory] = useState<StatusHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatusHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminAPI.getOrderStatusHistory(orderId);
      setHistory(response.data.data.statusHistory);
    } catch (error: any) {
      console.error('Error fetching status history:', error);
      setError('Failed to load status history');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (isOpen && orderId) {
      fetchStatusHistory();
    }
  }, [isOpen, orderId, fetchStatusHistory]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'preparing': return 'bg-orange-100 text-orange-800';
      case 'out_for_delivery': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatStatus = (status: string) => {
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${Z_INDEX_CLASSES.MODAL_OVERLAY} p-4`} style={{ zIndex: 60 }}>
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Order Status History</h3>
            <p className="text-sm text-gray-500 mt-1">Order #{orderId} - Complete tracking timeline</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading status history...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-500 text-xl mb-4">⚠️</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load History</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={fetchStatusHistory}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 text-xl mb-4">📋</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Status History</h3>
              <p className="text-gray-600">This order hasn't had any status changes yet.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {history.map((entry, index) => (
                <div key={entry.id} className="relative">
                  {/* Timeline line */}
                  {index < history.length - 1 && (
                    <div className="absolute left-6 top-12 w-0.5 h-16 bg-gray-200"></div>
                  )}
                  
                  <div className="flex items-start space-x-4">
                    {/* Status indicator */}
                    <div className="flex-shrink-0">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        entry.newStatus === 'delivered' ? 'bg-green-100' :
                        entry.newStatus === 'cancelled' ? 'bg-red-100' :
                        entry.newStatus === 'pending' ? 'bg-yellow-100' :
                        'bg-blue-100'
                      }`}>
                        {entry.newStatus === 'delivered' ? (
                          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : entry.newStatus === 'cancelled' ? (
                          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        ) : (
                          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="bg-gray-50 rounded-lg p-4">
                        {/* Status change */}
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm text-gray-600">Status changed from</span>
                          {entry.previousStatus ? (
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(entry.previousStatus)}`}>
                              {formatStatus(entry.previousStatus)}
                            </span>
                          ) : (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                              Initial
                            </span>
                          )}
                          <span className="text-sm text-gray-600">to</span>
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(entry.newStatus)}`}>
                            {formatStatus(entry.newStatus)}
                          </span>
                        </div>

                        {/* Change details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600">
                              <span className="font-medium">Changed by:</span>{' '}
                              {entry.changedByUser ? (
                                <span className="text-gray-900">
                                  {entry.changedByUser.name} ({entry.changedByUser.role})
                                </span>
                              ) : (
                                <span className="text-gray-500 italic">System</span>
                              )}
                            </p>
                            <p className="text-gray-600">
                              <span className="font-medium">Date & Time:</span>{' '}
                              <span className="text-gray-900">{formatDate(entry.createdAt)}</span>
                            </p>
                          </div>
                          <div>
                            {entry.changeReason && (
                              <p className="text-gray-600">
                                <span className="font-medium">Reason:</span>{' '}
                                <span className="text-gray-900">{entry.changeReason}</span>
                              </p>
                            )}
                            {entry.notes && (
                              <p className="text-gray-600">
                                <span className="font-medium">Notes:</span>{' '}
                                <span className="text-gray-900">{entry.notes}</span>
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Technical details (collapsible) */}
                        {(entry.ipAddress || entry.userAgent) && (
                          <details className="mt-3">
                            <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                              Technical Details
                            </summary>
                            <div className="mt-2 p-2 bg-white rounded border text-xs text-gray-600 space-y-1">
                              {entry.ipAddress && (
                                <p><span className="font-medium">IP:</span> {entry.ipAddress}</p>
                              )}
                              {entry.userAgent && (
                                <p><span className="font-medium">User Agent:</span> {entry.userAgent}</p>
                              )}
                            </div>
                          </details>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderStatusHistory;
